/**
 * Ficha do beneficiário › "Uso da rede (últimos 12 meses)": onde a pessoa se
 * atende (rede própria, Unimed, outras), para o atendente orientar o uso da
 * rede própria. Soma todas as matrículas do CPF.
 *
 * O destaque é o ambulatorial (consultas, exames, terapias): é onde existe
 * escolha. Internação vem à parte. A lista "o que dá para trazer" mostra o que
 * a pessoa fez fora e a rede própria também faz, com quem faz.
 * Backend: GET /beneficiarios/cpf/{cpf}/uso-rede (uso_rede_service.py).
 */
import React, { useEffect, useState } from 'react';
import { Network, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import InfoTip from './InfoTip';

const COR = { DIRETA: '#20a490', INTERCAMBIO: '#3498db', RECIPROCIDADE: '#9b59b6', FARMACIA: '#f39c12', REEMBOLSO: '#95a5a6' };
const corDe = (rede) => COR[rede] || '#c8d0d6';
const fmtP = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`);
const fmtR = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
const dataBR = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');
const titulo = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' };

const TIP = 'Onde a pessoa se atendeu nos últimos 12 meses, somando todas as matrículas do CPF, pelas contas médicas já apresentadas. '
  + 'Um atendimento é um dia num prestador. A barra principal é o ambulatorial médico (consultas, exames, terapias e pronto-socorro), onde o beneficiário escolhe; '
  + 'internação, farmácia, odonto e reembolso aparecem à parte. A rede de cada prestador vem da classificação da operadora (Inteligência de Custo). '
  + 'Limite: contas chegam semanas depois do atendimento, e a tela não sabe onde a pessoa mora, então a rede própria pode não ser opção para ela.';
const TIP_OP = 'Consultas, exames, terapias e procedimentos que a pessoa fez fora da rede própria nos últimos 12 meses e que a rede própria também fez no mesmo período, '
  + 'com os prestadores da rede própria que mais fizeram. Consulta sugere só a mesma especialidade; pronto-socorro e materiais não entram. Antes de sugerir, confira se a pessoa tem acesso a esses prestadores (cidade, especialidade).';

function Barra({ itens }) {
  return (
    <div style={{ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', background: '#eef1f4' }}
         role="img" aria-label={itens.map((r) => `${r.rotulo} ${fmtP(r.pct_atendimentos)}`).join(', ')}>
      {itens.map((r) => (
        <div key={r.rede} title={`${r.rotulo}: ${r.atendimentos} atendimento(s), ${fmtP(r.pct_atendimentos)}`}
             style={{ width: `${r.pct_atendimentos || 0}%`, background: corDe(r.rede) }} />
      ))}
    </div>
  );
}

export default function UsoRede({ cpf }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    if (!cpf) return undefined;
    let vivo = true;
    setD(null); setErro(false);
    api.get(`/beneficiarios/cpf/${encodeURIComponent(cpf)}/uso-rede`)
      .then((r) => vivo && setD(r.data)).catch(() => vivo && setErro(true));
    return () => { vivo = false; };
  }, [cpf]);

  if (erro || (d && !d.disponivel)) return null;   // sem classificação na instalação: a seção não aparece
  const card = { background: '#fff', borderRadius: '12px', border: '1px solid #e8e8e8', padding: '20px', marginBottom: '16px' };
  if (!d) {
    return <div style={card}><div style={titulo}><Network size={14} /> Uso da rede (últimos 12 meses)</div>
      <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '10px' }}>Carregando…</div></div>;
  }
  const amb = d.ambulatorial || [];
  const propria = amb.find((r) => r.rede === 'DIRETA');
  const fora = amb.filter((r) => r.rede !== 'DIRETA').sort((a, b) => b.atendimentos - a.atendimentos)[0];
  const intern = (d.internacao || []).reduce((s, r) => s + r.atendimentos, 0);

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={titulo}><Network size={14} /> Uso da rede (últimos 12 meses) <InfoTip w={360} texto={TIP} /></div>
        {d.matriculas > 1 && <span style={{ fontSize: '0.7rem', color: '#888' }}>{d.matriculas} matrículas somadas</span>}
      </div>

      {d.total_atendimentos === 0 ? (
        <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '10px' }}>Nenhum atendimento ambulatorial nos últimos 12 meses (desde {dataBR(d.desde)}).</div>
      ) : (
        <>
          <div style={{ fontSize: '0.92rem', color: '#2c3e50', margin: '12px 0 8px', lineHeight: 1.5 }}>
            <b style={{ fontSize: '1.3rem', color: corDe('DIRETA') }}>{fmtP(d.pct_rede_propria)}</b> dos atendimentos ambulatoriais foram na <b>rede própria</b>
            {fora && <> e <b style={{ color: corDe(fora.rede) }}>{fmtP(fora.pct_atendimentos)}</b> no <b>{fora.rotulo}</b></>}
            <span style={{ color: '#888', fontSize: '0.8rem' }}> ({d.total_atendimentos} atendimentos)</span>
          </div>
          <Barra itens={amb} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
            {amb.map((r) => (
              <span key={r.rede} style={{ fontSize: '0.74rem', color: '#555', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: corDe(r.rede) }} />
                {r.rotulo}: {r.atendimentos} ({fmtP(r.pct_atendimentos)}) · {fmtR(r.valor)}
              </span>
            ))}
          </div>
          {intern > 0 && (
            <div style={{ fontSize: '0.76rem', color: '#888', marginTop: '8px' }}>
              Internação, à parte: {(d.internacao || []).map((r) => `${r.rotulo} ${fmtR(r.valor)}`).join('; ')}.
            </div>
          )}
          {(d.outros || []).length > 0 && (
            <div style={{ fontSize: '0.76rem', color: '#888', marginTop: '4px' }}>
              Também à parte: {d.outros.map((r) => `${r.rotulo} ${r.atendimentos} (${fmtR(r.valor)})`).join('; ')}.
            </div>
          )}
          {propria === undefined && <div style={{ fontSize: '0.76rem', color: '#b9770e', marginTop: '6px' }}>Nenhum atendimento ambulatorial na rede própria no período.</div>}

          {d.oportunidades?.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
              <button onClick={() => setAberto((v) => !v)} aria-expanded={aberto}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#2c3e50', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {aberto ? <ChevronUp size={14} /> : <ChevronDown size={14} />} O que dá para fazer na rede própria ({d.oportunidades.length})
              </button> <InfoTip w={340} texto={TIP_OP} />
              {aberto && (
                <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                  {d.oportunidades.map((o) => (
                    <div key={o.procedimento} style={{ padding: '8px 10px', background: '#fafafa', border: '1px solid #eee', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.8rem', color: '#333' }}>
                        <b>{o.descricao}</b>{o.especialidade_nome ? <> ({o.especialidade_nome.toLowerCase()})</> : null} <span style={{ color: '#999' }}>{o.procedimento}</span>
                      </div>
                      <div style={{ fontSize: '0.74rem', color: '#777', marginTop: '2px' }}>
                        {o.atendimentos}x pelo {o.onde}. Na rede própria: {o.rede_propria.map((p) => p.nome).join(', ')}.
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
